/**
 * Settings → Alerts & Reports → Report subscriptions (T-068).
 *
 * A read view of every report subscription in the caller's scope and its
 * recipients (app users and outside emails), with a by-subscription and a
 * by-recipient tab. The server (reportAdminScope) owns authz + company-bound
 * redaction; this page renders what it returns. The only mutation here is
 * pause/resume; recipient add/remove lives in Reports (deep link).
 *
 * Design: mirrors the existing Settings/Reports pages — semantic tokens only,
 * tabular-nums on numeric/date columns, brand green (#8FB560) for ACTIVE and
 * coral (#E0556C) for suppressed/failed ONLY, dark-mode default. No new visual
 * language.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardList, Loader2, Download, ExternalLink, ChevronRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchInput } from '@/components/ui/search-input';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { updateReportSubscription } from '@/lib/reportSubscriptionsApi';
import {
  getReportInventory,
  downloadReportInventoryCsv,
  type InventoryResponse,
  type InventorySubscriptionRow,
  type InventoryRecipientRow,
  type InventoryView,
  type RecipientStatus,
  type RecipientReason,
  type InventoryLastAttempt,
} from '@/lib/reportInventoryApi';
import { attemptLabel } from '@/lib/reportAttemptLabel';

const ACTIVE_GREEN = '#8FB560';
const NEGATIVE_CORAL = '#E0556C';

/** T-212: what happened to the latest scheduled send, under its date. */
function LastAttemptLine({ attempt }: { attempt?: InventoryLastAttempt | null }) {
  const label = attemptLabel(attempt);
  if (!label) return null;
  return (
    <div className="mt-0.5 text-xs" style={label.tone === 'negative' ? { color: NEGATIVE_CORAL } : undefined}>
      {label.text}
    </div>
  );
}
const ALL = '__all__';

function reasonLabel(reason: RecipientReason): string {
  switch (reason) {
    case 'complaint': return 'Spam complaint';
    case 'bounce': return 'Bounced';
    case 'unsubscribed': return 'Unsubscribed';
    case 'reports_suppression': return 'Opted out of reports';
    default: return '';
  }
}

/** A recipient/worst status pill — green only for active, coral only for suppressed/failed. */
function StatusPill({ status, reason }: { status: RecipientStatus; reason?: RecipientReason }) {
  if (status === 'active') {
    return <Badge variant="outline" style={{ color: ACTIVE_GREEN, borderColor: ACTIVE_GREEN }}>Active</Badge>;
  }
  if (status === 'unsubscribed') {
    return <Badge variant="outline" className="text-muted-foreground">Unsubscribed</Badge>;
  }
  // suppressed
  return (
    <Badge variant="outline" style={{ color: NEGATIVE_CORAL, borderColor: NEGATIVE_CORAL }}>
      {reason ? reasonLabel(reason) : 'Suppressed'}
    </Badge>
  );
}

function recipientDisplay(r: { email?: string | null; name?: string | null; redacted: boolean }): string {
  if (r.email) return r.name ? `${r.name} · ${r.email}` : r.email;
  if (r.redacted) return '(recipient outside this company)';
  return '(user without email)';
}

export function ReportSubscriptionsInventory() {
  const { is_superadmin, capabilities, companies } = useSettingsContext();

  const [view, setView] = useState<InventoryView>('subscription');
  const [companyId, setCompanyId] = useState<string>(ALL);
  const [cadence, setCadence] = useState<string>(ALL);
  const [enabled, setEnabled] = useState<string>(ALL);
  const [recipientType, setRecipientType] = useState<string>(ALL);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const [data, setData] = useState<InventoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [csvBusy, setCsvBusy] = useState(false);

  // Debounce the free-text search.
  const [debouncedQ, setDebouncedQ] = useState('');
  const qTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (qTimer.current) clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 300);
    return () => { if (qTimer.current) clearTimeout(qTimer.current); };
  }, [q]);

  const filters = useMemo(() => ({
    view,
    company_id: companyId === ALL ? undefined : companyId,
    cadence: cadence === ALL ? undefined : (cadence as 'daily' | 'weekly' | 'monthly'),
    enabled: enabled === ALL ? undefined : enabled === 'true',
    recipient_type: recipientType === ALL ? undefined : (recipientType as 'user' | 'external'),
    q: debouncedQ || undefined,
    page,
    per_page: 50,
  }), [view, companyId, cadence, enabled, recipientType, debouncedQ, page]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const res = await getReportInventory(filters);
      setData(res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      if (/forbidden/i.test(msg)) setForbidden(true);
      else setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { void load(); }, [load]);

  const togglePause = async (row: InventorySubscriptionRow) => {
    setBusyId(row.id);
    try {
      await updateReportSubscription(row.id, { enabled: !row.enabled });
      toast.success(row.enabled ? 'Subscription paused' : 'Subscription resumed');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const exportCsv = async () => {
    setCsvBusy(true);
    try {
      await downloadReportInventoryCsv(filters);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setCsvBusy(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const showCompanyFilter = (companies?.length ?? 0) > 1 || is_superadmin;
  const gate = data?.reports_gate;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.per_page)) : 1;

  if (!capabilities?.report_subscriptions_admin && forbidden) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">You don’t have access to report subscriptions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-md bg-muted p-2"><ClipboardList className="h-5 w-5 text-muted-foreground" /></div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight text-foreground">Report subscriptions</h1>
            <p className="text-sm text-muted-foreground">
              Every recurring report subscription and its recipients — app users and outside emails — with cadence and delivery status.
            </p>
          </div>
        </div>
        {is_superadmin && (
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={csvBusy || loading}>
            {csvBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Export CSV
          </Button>
        )}
      </div>

      {/* Reports-dispatch gate banner */}
      {gate && !gate.allowed && (
        <div
          className="flex items-start gap-2 rounded-md border p-3 text-sm"
          style={{ borderColor: NEGATIVE_CORAL }}
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: NEGATIVE_CORAL }} />
          <div className="text-muted-foreground">
            Report emails are not being sent right now — these subscriptions exist but nothing is delivered.
            {is_superadmin && (
              <> <Link to="/settings/alerts" className="underline">Manage the reports email route</Link>.</>
            )}
          </div>
        </div>
      )}

      {/* Tabs + filters */}
      <Tabs value={view} onValueChange={(v) => { setView(v as InventoryView); setPage(1); }}>
        <TabsList>
          <TabsTrigger value="subscription">By subscription</TabsTrigger>
          <TabsTrigger value="recipient">By recipient</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-64">
          <SearchInput value={q} onValueChange={setQ} placeholder="Search name or email…" />
        </div>
        {showCompanyFilter && (
          <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setPage(1); }}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Company" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All companies</SelectItem>
              {companies?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={cadence} onValueChange={(v) => { setCadence(v); setPage(1); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Cadence" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All cadences</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
        <Select value={enabled} onValueChange={(v) => { setEnabled(v); setPage(1); }}>
          <SelectTrigger className="w-36"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All states</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Paused</SelectItem>
          </SelectContent>
        </Select>
        <Select value={recipientType} onValueChange={(v) => { setRecipientType(v); setPage(1); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Recipients" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All recipients</SelectItem>
            <SelectItem value="user">App users</SelectItem>
            <SelectItem value="external">Outside emails</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="p-6 text-sm" style={{ color: NEGATIVE_CORAL }}>{error}</div>
          ) : forbidden ? (
            <div className="p-6 text-sm text-muted-foreground">You don’t have access to report subscriptions.</div>
          ) : !data || data.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No report subscriptions match these filters.</div>
          ) : data.view === 'subscription' ? (
            // Switch on data.view, not the pending local `view`: the loaded rows
            // keep the shape of the view they were fetched for until the refetch
            // lands, so rendering by local state would map the wrong shape.
            <SubscriptionTable
              rows={data.items as InventorySubscriptionRow[]}
              expanded={expanded}
              onToggle={toggleExpand}
              onPause={togglePause}
              busyId={busyId}
            />
          ) : (
            <RecipientTable rows={data.items as InventoryRecipientRow[]} />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > data.per_page && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="tabular-nums">{data.total} total</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <span className="tabular-nums">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SubscriptionTable({
  rows, expanded, onToggle, onPause, busyId,
}: {
  rows: InventorySubscriptionRow[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onPause: (row: InventorySubscriptionRow) => void;
  busyId: string | null;
}) {
  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');
  return (
    <Table className="min-w-[900px]">
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Subscription</TableHead>
          <TableHead>Source</TableHead>
          <TableHead>Company</TableHead>
          <TableHead>Scope</TableHead>
          <TableHead>Cadence</TableHead>
          <TableHead>Next send</TableHead>
          <TableHead>Last attempt</TableHead>
          <TableHead>Recipients</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const isOpen = expanded.has(row.id);
          return (
            <Fragment key={row.id}>
              <TableRow className={row.enabled ? '' : 'opacity-60'}>
                <TableCell>
                  <button aria-label={isOpen ? 'Collapse' : 'Expand'} onClick={() => onToggle(row.id)} className="text-muted-foreground">
                    <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </button>
                </TableCell>
                <TableCell className="font-medium text-foreground">{row.name}</TableCell>
                <TableCell className="text-muted-foreground">{row.source === 'portal' ? 'Document Portal' : 'Reports'}</TableCell>
                <TableCell className="text-muted-foreground">{row.company.name || '—'}</TableCell>
                <TableCell className="max-w-48 text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <span className="truncate" title={row.scope.label}>{row.scope.label}</span>
                    {!row.scope.enforced && (
                      <InfoTooltip content="Descriptive only — a subscription's scope filters are not yet applied when reports are delivered (ISS-065). Every recipient still receives the company-wide report until that ships." />
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{row.schedule_label}</TableCell>
                <TableCell className="tabular-nums text-muted-foreground">{fmtDate(row.next_due_at)}</TableCell>
                <TableCell className="tabular-nums text-muted-foreground">
                  {fmtDate(row.last_attempt_at)}
                  <LastAttemptLine attempt={row.last_attempt} />
                </TableCell>
                <TableCell className="tabular-nums text-muted-foreground">
                  {row.recipient_counts.users} user{row.recipient_counts.users === 1 ? '' : 's'} · {row.recipient_counts.external} external
                  {row.recipient_counts.inactive > 0 && (
                    <span style={{ color: NEGATIVE_CORAL }}> · {row.recipient_counts.inactive} inactive</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" disabled={busyId === row.id} onClick={() => onPause(row)}>
                      {busyId === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : row.enabled ? 'Pause' : 'Resume'}
                    </Button>
                    <Button asChild variant="ghost" size="sm">
                      <Link to="/reports" title="Open in Reports"><ExternalLink className="h-4 w-4" /></Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              {isOpen && (
                <TableRow className="bg-muted/30">
                  <TableCell />
                  <TableCell colSpan={9} className="py-3">
                    <div className="space-y-1">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Owner: {row.owner.redacted ? '(owner outside this company)' : recipientDisplay(row.owner)}
                      </div>
                      {row.recipients.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No recipients.</div>
                      ) : row.recipients.map((rc) => (
                        <div key={rc.id} className={`flex items-center gap-2 text-sm ${rc.status === 'active' ? '' : 'opacity-70'}`}>
                          <span className="text-foreground">{recipientDisplay(rc)}</span>
                          <span className="text-xs text-muted-foreground">{rc.kind === 'user' ? 'App user' : 'Outside email'}</span>
                          <StatusPill status={rc.status} reason={rc.primary_reason} />
                        </div>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

function RecipientTable({ rows }: { rows: InventoryRecipientRow[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Recipient</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Subscriptions</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key} className={row.worst_status === 'active' ? '' : 'opacity-80'}>
            <TableCell className="font-medium text-foreground">
              {row.email || (row.redacted ? '(recipient outside this company)' : '(user without email)')}
              {row.display_name && <span className="ml-1 text-xs text-muted-foreground">{row.display_name}</span>}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {row.kinds.map((k) => (k === 'user' ? 'App user' : 'Outside email')).join(' + ')}
            </TableCell>
            <TableCell className="text-muted-foreground">
              <span className="tabular-nums">{row.subscription_count}</span>
              <span className="ml-2 text-xs">
                {row.subscriptions.slice(0, 3).map((s) => `${s.name} (${s.cadence})`).join(', ')}
                {row.subscriptions.length > 3 ? ` +${row.subscriptions.length - 3}` : ''}
              </span>
            </TableCell>
            <TableCell><StatusPill status={row.worst_status} reason={row.worst_reason} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
